import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Play, Square, Save, Download, Upload, Loader2, Zap, ChevronLeft } from "lucide-react";
import api, { formatApiErrorDetail, downloadAuthedFile } from "@/lib/api";
import LightbarViewport from "./LightbarViewport";

const COLORS = [
  { id: "red", hex: "#ff2d2d" }, { id: "blue", hex: "#2e6bff" }, { id: "white", hex: "#f5f5f5" },
  { id: "amber", hex: "#ffb02e" }, { id: "green", hex: "#00e676" }, { id: "cyan", hex: "#22d3ee" },
];
const hexOf = (id) => (COLORS.find((c) => c.id === id) || COLORS[2]).hex;

const DEFAULT_MODULES = [
  { label: "Left Outer", color: "red" }, { label: "Left Inner", color: "red" },
  { label: "Center", color: "white" }, { label: "Right Inner", color: "blue" }, { label: "Right Outer", color: "blue" },
];

function makeFrames(modules, count) {
  return Array.from({ length: count }, (_, i) => modules.map((_, mi) => (i % 2 === 0 ? (mi < modules.length / 2 ? 1 : 0) : (mi >= modules.length / 2 ? 1 : 0))));
}

export default function LightbarStudio() {
  const [me, setMe] = useState(null);
  const [modules, setModules] = useState(DEFAULT_MODULES);
  const [frames, setFrames] = useState(() => makeFrames(DEFAULT_MODULES, 8));
  const [speed, setSpeed] = useState(120);
  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState(0);
  const [name, setName] = useState("Pursuit Pattern Alpha");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [itemId, setItemId] = useState("");
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => { setMe(r.data); loadMine(); }).catch(() => setMe(null));
  }, []);

  const loadMine = () => api.get("/ncmod/lightbar/mine").then((r) => setMine(r.data.items || [])).catch(() => {});

  useEffect(() => {
    if (playing) {
      timer.current = setInterval(() => setPlayhead((p) => (p + 1) % frames.length), speed);
    } else if (timer.current) {
      clearInterval(timer.current);
    }
    return () => timer.current && clearInterval(timer.current);
  }, [playing, speed, frames.length]);

  const toggleCell = (mi, fi) => {
    setFrames((f) => f.map((frame, idx) => (idx === fi ? frame.map((v, j) => (j === mi ? (v ? 0 : 1) : v)) : frame)));
  };

  const addModule = () => {
    if (modules.length >= 12) return;
    setModules((m) => [...m, { label: `Module ${m.length + 1}`, color: "white" }]);
    setFrames((f) => f.map((frame) => [...frame, 0]));
  };

  const removeModule = (mi) => {
    if (modules.length <= 1) return;
    setModules((m) => m.filter((_, i) => i !== mi));
    setFrames((f) => f.map((frame) => frame.filter((_, i) => i !== mi)));
  };

  const resizeFrames = (n) => {
    const count = Math.max(2, Math.min(64, n));
    setFrames((f) => {
      if (count > f.length) return [...f, ...Array.from({ length: count - f.length }, () => modules.map(() => 0))];
      return f.slice(0, count);
    });
  };

  const config = () => ({ modules, frames, speed });

  const save = async () => {
    if (!me) { toast.error("Sign in to save patterns"); return; }
    setBusy(true);
    try {
      const payload = { name, description, config: config(), public: isPublic };
      const r = itemId ? await api.patch(`/ncmod/lightbar/${itemId}`, payload) : await api.post("/ncmod/lightbar", payload);
      setItemId(r.data.id);
      toast.success(itemId ? "Pattern updated" : "Pattern saved");
      loadMine();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const loadItem = (item) => {
    setItemId(item.id);
    setName(item.name);
    setDescription(item.description || "");
    setModules(item.config.modules);
    setFrames(item.config.frames);
    setSpeed(item.config.speed || 120);
    setIsPublic(item.public || false);
    setPlaying(false);
    toast.success(`Loaded "${item.name}"`);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify({ type: "nc-lightbar-pattern", version: 1, name, ...config() }, null, 2)], { type: "application/json" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `${name.replace(/\s+/g, "_")}.nclight.json` });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportCSV = () => {
    if (!itemId || !isPublic) { toast.error("Save + share publicly first to get the UE5 DataTable CSV"); return; }
    downloadAuthedFile(`/ncmod/lightbar/${itemId}/export.csv`, `${name.replace(/\s+/g, "_")}.csv`).catch((e) => toast.error(e.message));
  };

  const activeFrame = frames[playing ? playhead : -1] || [];

  return (
    <div data-testid="lightbar-studio" className="flex flex-col gap-6">
      {/* live 3D preview */}
      <div>
        <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase mb-3 flex items-center gap-2"><Zap className="w-3.5 h-3.5" /> Live preview // drag to orbit</p>
        <div data-testid="lightbar-preview">
          <LightbarViewport modules={modules} activeFrame={playing ? activeFrame : modules.map(() => 0)} />
        </div>
        <div className="flex items-center gap-4 mt-4 flex-wrap">
          <button data-testid="preview-play" onClick={() => setPlaying((p) => !p)} className="font-mono text-[10px] tracking-[0.2em] uppercase bg-nc-blue hover:bg-nc-blue/80 text-white px-4 py-2.5 transition-colors flex items-center gap-2">
            {playing ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />} {playing ? "Stop" : "Play"}
          </button>
          <div className="flex items-center gap-2 flex-1 min-w-48">
            <span className="font-mono text-[9px] text-zinc-500 uppercase">Speed</span>
            <input data-testid="speed-slider" type="range" min="40" max="400" step="10" value={440 - speed} onChange={(e) => setSpeed(440 - Number(e.target.value))} className="flex-1 accent-nc-orange" />
            <span className="font-mono text-[9px] text-nc-orange w-14">{speed}ms</span>
          </div>
        </div>
      </div>

      {/* editor grid */}
      <div className="border border-white/10 bg-black/40 p-6 overflow-x-auto">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-orange uppercase">Pattern editor — click cells to toggle</p>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[9px] text-zinc-500 uppercase">Frames</span>
            <input data-testid="frames-count" type="number" min="2" max="64" value={frames.length} onChange={(e) => resizeFrames(Number(e.target.value))} className="w-16 bg-[#050505] border border-white/10 px-2 py-1.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" />
            <button data-testid="add-module" onClick={addModule} className="font-mono text-[9px] uppercase border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-2.5 py-1.5 transition-colors flex items-center gap-1"><Plus className="w-3 h-3" /> Module</button>
          </div>
        </div>
        <table data-testid="pattern-grid" className="border-collapse">
          <thead>
            <tr>
              <th className="font-mono text-[8px] text-zinc-600 uppercase text-left pr-3 pb-2">Module</th>
              {frames.map((_, fi) => (
                <th key={fi} className={`font-mono text-[8px] pb-2 w-8 ${playing && playhead === fi ? "text-nc-orange" : "text-zinc-600"}`}>{fi + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modules.map((m, mi) => (
              <tr key={mi}>
                <td className="pr-3 py-1">
                  <div className="flex items-center gap-1.5">
                    <select value={m.color} onChange={(e) => setModules((ms) => ms.map((x, i) => (i === mi ? { ...x, color: e.target.value } : x)))} className="bg-[#050505] border border-white/10 font-mono text-[9px] px-1 py-1 rounded-none focus:outline-none" style={{ color: hexOf(m.color) }}>
                      {COLORS.map((c) => <option key={c.id} value={c.id}>{c.id}</option>)}
                    </select>
                    <input value={m.label} onChange={(e) => setModules((ms) => ms.map((x, i) => (i === mi ? { ...x, label: e.target.value.slice(0, 18) } : x)))} className="w-24 bg-[#050505] border border-white/10 px-1.5 py-1 font-mono text-[9px] text-zinc-200 rounded-none focus:outline-none" />
                    <button onClick={() => removeModule(mi)} className="text-zinc-600 hover:text-red-400 transition-colors"><Trash2 className="w-3 h-3" /></button>
                  </div>
                </td>
                {frames.map((frame, fi) => (
                  <td key={fi} className="p-0.5">
                    <button
                      data-testid={`cell-${mi}-${fi}`}
                      onClick={() => toggleCell(mi, fi)}
                      className={`w-7 h-7 border transition-all duration-100 ${playing && playhead === fi ? "ring-1 ring-nc-orange" : ""}`}
                      style={{
                        background: frame[mi] ? hexOf(m.color) : "#0a0a0a",
                        borderColor: frame[mi] ? hexOf(m.color) : "rgba(255,255,255,0.12)",
                        boxShadow: frame[mi] ? `0 0 8px ${hexOf(m.color)}88` : "none",
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* save / share / export */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase">Save & share</p>
          <input data-testid="pattern-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className="bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" placeholder="Pattern name" />
          <input value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} className="bg-[#050505] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue" placeholder="Short description (optional)" />
          <label className="flex items-center gap-2 font-mono text-[10px] text-zinc-400 cursor-pointer">
            <input data-testid="pattern-public" type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="accent-nc-orange" />
            Share publicly in the Mod Hub library
          </label>
          <div className="flex gap-2 flex-wrap">
            <button data-testid="pattern-save" onClick={save} disabled={busy || name.trim().length < 3} className="font-mono text-[10px] tracking-[0.15em] uppercase bg-nc-blue hover:bg-nc-blue/80 disabled:opacity-40 text-white px-4 py-2.5 transition-colors flex items-center gap-2">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {itemId ? "Update" : "Save"}
            </button>
            <button data-testid="export-json" onClick={exportJSON} className="font-mono text-[10px] uppercase border border-white/20 text-zinc-300 hover:border-nc-blue hover:text-nc-blue px-4 py-2.5 transition-colors flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> .nclight.json
            </button>
            <button data-testid="export-csv" onClick={exportCSV} className="font-mono text-[10px] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-4 py-2.5 transition-colors flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> UE5 DataTable CSV
            </button>
          </div>
          <p className="font-mono text-[8px] text-zinc-600 leading-relaxed">CSV drops straight into UE5 as a DataTable — your mod reads frames and drives the lightbar modules. Share publicly to unlock it.</p>
        </div>

        <div className="border border-white/10 bg-black/40 p-5 flex flex-col gap-2">
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-500 uppercase mb-1">My saved patterns</p>
          {!me && <p className="font-mono text-[10px] text-zinc-600">Sign in to save and load your patterns.</p>}
          {me && mine.length === 0 && <p className="font-mono text-[10px] text-zinc-600">Nothing saved yet — build a pattern and hit Save.</p>}
          {mine.map((item) => (
            <div key={item.id} data-testid={`mine-${item.id.slice(0, 8)}`} className="flex items-center justify-between border border-white/5 bg-[#050505] px-3 py-2">
              <button onClick={() => loadItem(item)} className="font-mono text-[10px] text-zinc-200 hover:text-nc-blue transition-colors text-left flex items-center gap-2">
                <Upload className="w-3 h-3 text-zinc-600" /> {item.name}
              </button>
              <button onClick={async () => { await api.delete(`/ncmod/lightbar/${item.id}`); loadMine(); if (itemId === item.id) setItemId(""); }} className="text-zinc-600 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
